import type { Backdrop, PersonPose, Prop } from "./scene.js";

/**
 * The examples by business type (2026-10-13, the owner: "have some example of business type like plumber pharmacy …
 * so all the style would display the same text for this business type that we can change"). Each is one made-up
 * business, written once here: its colours, the words of its videos, and what its painted scenes show. Every drawn
 * style is drawn from the same sheet (`exampleComposition`, apps/render/src/examples.ts), keeping that style's own run of
 * scenes and their lengths from the gallery (`GALLERY`), so a type's ten videos tell one story in ten looks. A style
 * shows the parts of the sheet its scenes use: Bold the hook, the figure, the claim and the offer; Showreel nearly all.
 *
 * Everything here is made up, the figures included, and every screen that shows these says so. The words are written the
 * way `write_script` writes a drawn video, not by it. Change a line, draw the type again
 * (`pnpm --filter @joogoos/render examples <type>`), and copy its files where they are shown (docs/03 § the gallery).
 */
export interface ExampleBusiness {
  /** The type's id: its folder under `examples/`. */
  id: string;
  /** What the switch says ("Plumber"). */
  type: string;
  /** The made-up business. */
  name: string;
  /** Its website or phone number, as the offer and the close show it; always `.example` or a drama number. */
  address: string;
  /** Its colours, dark, accent and light, as a customer's brand would give them. */
  palette: readonly [string, string, string];
  /** What every video opens on: a question in the viewer's own words, and its answer. */
  hook: readonly [string, string];
  /** What goes wrong for the customer elsewhere (a showreel's reveal). */
  problems: readonly [string, string, string];
  /** How it works, in three steps, under its heading, and the word stamped over the end where a style stamps one. */
  steps: { headline: string; items: ReadonlyArray<{ title: string; detail: string }>; stamp: string };
  /** One number, made up like the rest: counted up to, with what leads into it and what follows it. */
  figure: { value: number; prefix: string; suffix: string; lead: string; tail: string; caption: string };
  /** Three short things the customer gets (a showreel's flip). */
  claims: readonly [string, string, string];
  /** A second headline, for the styles that have one, and the words of it drawn in the accent. */
  claim: { headline: string; accent: string };
  /** The last thing seen. */
  offer: { headline: string; button: string };
  /** What a check turns up (Notebook's document). */
  document: { headline: string; title: string; lines: ReadonlyArray<{ label: string; amount: string; flag: string | null }>; totalLabel: string; total: string };
  /** Storybook's two painted scenes: the first carries the hook, the second its own caption. */
  painted: readonly [
    { backdrop: Backdrop; cast: readonly Prop[]; pose: PersonPose | null; flags?: readonly string[] },
    { backdrop: Backdrop; cast: readonly Prop[]; pose: PersonPose | null; caption: string; sub: string },
  ];
}

export const EXAMPLE_BUSINESSES: readonly ExampleBusiness[] = [
  {
    id: "plumber", type: "Plumber", name: "Dale & Sons Plumbing", address: "01632 960 412",
    palette: ["#1f2a44", "#2f6fed", "#ffffff"],
    hook: ["Burst pipe?", "Stopped tonight."],
    problems: ["A hidden call-out fee", "Weekend rate on a Tuesday", "A quote that grew"],
    steps: { headline: "How we quote.", stamp: "FIXED", items: [
      { title: "Send us a photo", detail: "of the leak or the boiler" },
      { title: "Get one fixed price", detail: "before we start" },
      { title: "Pay when it works", detail: "not a minute before" },
    ] },
    figure: { value: 60, prefix: "", suffix: " min", lead: "At your door in", tail: "day or night", caption: "to your door, day or night" },
    claims: ["Family-run.", "No call-out fee.", "No weekend rates."],
    claim: { headline: "One price, agreed first.", accent: "agreed first." },
    offer: { headline: "No surprises on the bill.", button: "Get a fixed quote" },
    document: { headline: "What nobody mentioned.", title: "Quote · boiler repair", totalLabel: "Added on", total: "£95", lines: [
      { label: "Labour, one hour", amount: "£60", flag: null },
      { label: "Call-out fee", amount: "£45", flag: "not said on the phone" },
      { label: "Parts", amount: "£38", flag: null },
      { label: "Weekend rate", amount: "£50", flag: "it was a Tuesday" },
    ] },
    painted: [
      { backdrop: "night", cast: ["houses", "van"], pose: null },
      { backdrop: "street", cast: ["person", "van", "houses"], pose: "walk", caption: "With you in an hour.", sub: "and no call-out fee, ever" },
    ],
  },
  {
    id: "pharmacy", type: "Pharmacy", name: "Brook Lane Pharmacy", address: "brooklanepharmacy.example",
    palette: ["#0f3d3a", "#2bb673", "#f4fbf8"],
    hook: ["Tablets running low?", "We'll text when it's ready."],
    problems: ["A queue to the door", "A repeat that wasn't ready", "Closed by the time you're free"],
    steps: { headline: "Repeats, sorted.", stamp: "READY", items: [
      { title: "Order your repeat", detail: "on our app or by phone" },
      { title: "We get it ready", detail: "checked by the pharmacist" },
      { title: "Collect after work", detail: "or we bring it round" },
    ] },
    figure: { value: 8, prefix: "", suffix: " pm", lead: "Open every weekday till", tail: "so you can come after work", caption: "when we close on weekdays, so you can come after work" },
    claims: ["Free blood pressure checks.", "Flu jabs, no appointment.", "Delivery on Thursdays."],
    claim: { headline: "Your repeat, ready when you are.", accent: "ready when you are." },
    offer: { headline: "One trip. That's it.", button: "Order a repeat" },
    document: { headline: "What a repeat used to cost you.", title: "Tuesday, lunch break", totalLabel: "Lunch lost", total: "45 min", lines: [
      { label: "Queue at the counter", amount: "25 min", flag: "most of your lunch" },
      { label: "Second trip", amount: "20 min", flag: "not ready, nobody rang" },
      { label: "Parking", amount: "£3", flag: null },
    ] },
    painted: [
      { backdrop: "day", cast: ["shop", "van"], pose: null },
      { backdrop: "street", cast: ["person", "shop", "houses"], pose: "carry", caption: "We bring it round.", sub: "on Thursdays, if you can't come in" },
    ],
  },
  {
    id: "accountant", type: "Accountant", name: "Marsh & Co Accountants", address: "marshandco.example",
    palette: ["#1b2a3a", "#e0a526", "#fbf8f1"],
    hook: ["Tax return in a drawer?", "Filed this week."],
    problems: ["Receipts in three shoeboxes", "A penalty letter in the post", "Expenses you forgot to claim"],
    steps: { headline: "How it works.", stamp: "FILED", items: [
      { title: "Photograph your receipts", detail: "in the app, as they come" },
      { title: "We do the books", detail: "every month, by the 10th" },
      { title: "You sign, we file", detail: "weeks before the deadline" },
    ] },
    figure: { value: 95, prefix: "£", suffix: " a month", lead: "Books and return done for", tail: "no hourly bills", caption: "books and tax return done, one fixed fee" },
    claims: ["One fixed fee.", "A real accountant.", "Never a late filing."],
    claim: { headline: "Deadlines are ours.", accent: "ours." },
    offer: { headline: "Bring the shoebox.", button: "Book a free call" },
    document: { headline: "What your last return missed.", title: "Tax return · 2024–25", totalLabel: "Left on the table", total: "£1,840", lines: [
      { label: "Home office", amount: "£312", flag: "never claimed" },
      { label: "Van mileage", amount: "£1,125", flag: "half the year missing" },
      { label: "Phone and internet", amount: "£303", flag: "forgotten" },
      { label: "Late filing", amount: "£100", flag: "one day over" },
    ] },
    painted: [
      { backdrop: "office", cast: ["desk", "person"], pose: "sit" },
      { backdrop: "office", cast: ["desk", "person", "coins"], pose: "phone", caption: "Filed. Weeks early.", sub: "and every expense you were owed" },
    ],
  },
  {
    id: "it-support", type: "IT support", name: "Northgate IT", address: "northgateit.example",
    palette: ["#101827", "#6366f1", "#f5f7fb"],
    hook: ["Wi-Fi down again?", "Fixed before lunch."],
    problems: ["One password for everything", "Backups nobody has checked", "A laptop only Dave can fix"],
    steps: { headline: "How we help.", stamp: "FIXED", items: [
      { title: "Call or email us", detail: "one number for the whole office" },
      { title: "We fix it remotely", detail: "most jobs within the hour" },
      { title: "We stop it coming back", detail: "and tell you what we did" },
    ] },
    figure: { value: 60, prefix: "", suffix: " min", lead: "Most problems fixed in", tail: "without anyone coming in", caption: "most problems fixed, without anyone coming in" },
    claims: ["Local engineers.", "One monthly price.", "Backups checked weekly."],
    claim: { headline: "IT that just works.", accent: "just works." },
    offer: { headline: "A free check of your IT.", button: "Book a check" },
    document: { headline: "What the health check found.", title: "Office check · 12 desks", totalLabel: "Risks found", total: "4", lines: [
      { label: "Backups", amount: "last run March", flag: "six months old" },
      { label: "Passwords", amount: "one for all", flag: "on a sticky note" },
      { label: "Windows updates", amount: "off", flag: "on four laptops" },
      { label: "Antivirus", amount: "expired", flag: "since June" },
    ] },
    painted: [
      { backdrop: "office", cast: ["desk", "person"], pose: "phone" },
      { backdrop: "office", cast: ["desk", "person"], pose: "sit", caption: "Sorted in an hour.", sub: "and we tell you what we fixed" },
    ],
  },
  {
    id: "office-cleaning", type: "Office cleaning", name: "Brightway Cleaning", address: "brightwaycleaning.example",
    palette: ["#06334a", "#00a6a6", "#f2fbfb"],
    hook: ["Monday office mess?", "Spotless before eight."],
    problems: ["Bins left full all weekend", "A kitchen nobody wants to use", "Cleaners who change every week"],
    steps: { headline: "How we clean.", stamp: "CHECKED", items: [
      { title: "Tell us your hours", detail: "we clean around them" },
      { title: "Same team, every visit", detail: "who know your office" },
      { title: "Checked every week", detail: "by a supervisor" },
    ] },
    figure: { value: 6, prefix: "", suffix: " am", lead: "Done every weekday by", tail: "before anyone arrives", caption: "done every weekday, before anyone arrives" },
    claims: ["Supplies included.", "Fully insured.", "No long contracts."],
    claim: { headline: "Walk into a clean office.", accent: "clean office." },
    offer: { headline: "Next-day quotes.", button: "Get a quote" },
    document: { headline: "What your last cleaner skipped.", title: "Monday morning · 3rd floor", totalLabel: "Missed", total: "3 of 4", lines: [
      { label: "Kitchen", amount: "done", flag: "mugs from Friday" },
      { label: "Bins", amount: "emptied", flag: "full all weekend" },
      { label: "Meeting room", amount: "done", flag: "crumbs on the table" },
      { label: "Toilets", amount: "done", flag: null },
    ] },
    painted: [
      { backdrop: "night", cast: ["shop", "van"], pose: null },
      { backdrop: "office", cast: ["desk", "person"], pose: "wave", caption: "Bins out, kitchen clean.", sub: "before your first meeting" },
    ],
  },
  {
    id: "cafe", type: "Café", name: "Kettle & Crumb", address: "kettleandcrumb.example",
    palette: ["#2a1b14", "#d9822b", "#fdf6ee"],
    hook: ["Ten minutes for lunch?", "Ready as you walk in."],
    problems: ["A queue round the corner", "A sandwich made yesterday", "Coffee that tastes of nothing"],
    steps: { headline: "Lunch, without the queue.", stamp: "READY", items: [
      { title: "Order on your phone", detail: "before you leave the office" },
      { title: "We make it fresh", detail: "the moment your order lands" },
      { title: "Collect your lunch", detail: "no queue, no wait" },
    ] },
    figure: { value: 7, prefix: "£", suffix: " the lot", lead: "Soup, sandwich and coffee", tail: "every weekday lunchtime", caption: "soup, sandwich and coffee, every weekday" },
    claims: ["Baked this morning.", "Beans from down the road.", "Open from 7 am."],
    claim: { headline: "Lunch, ready on time.", accent: "ready on time." },
    offer: { headline: "First coffee is on us.", button: "Order ahead" },
    document: { headline: "What your lunch break cost.", title: "Wednesday · 12:30", totalLabel: "Lunch break left", total: "7 min", lines: [
      { label: "Queue", amount: "14 min", flag: "before you ordered" },
      { label: "Wait for food", amount: "9 min", flag: "after you paid" },
      { label: "Sandwich", amount: "£6.50", flag: "made yesterday" },
      { label: "Coffee", amount: "£3.40", flag: null },
    ] },
    painted: [
      { backdrop: "day", cast: ["shop", "tree"], pose: null },
      { backdrop: "street", cast: ["person", "shop", "tree"], pose: "carry", caption: "Fresh, and ready to go.", sub: "pick it up on your way back" },
    ],
  },
  {
    id: "dental", type: "Dental clinic", name: "Riverside Dental", address: "riversidedental.example",
    palette: ["#123047", "#2a9d8f", "#f7fbfb"],
    hook: ["Putting off the dentist?", "We'll be gentle."],
    problems: ["Appointments that run late", "A bill you didn't expect", "Weeks before you're seen"],
    steps: { headline: "Booked in three taps.", stamp: "BOOKED", items: [
      { title: "Book online", detail: "from your phone, any day" },
      { title: "Get a reminder", detail: "the day before" },
      { title: "Walk straight in", detail: "seen at the time you booked" },
    ] },
    figure: { value: 12, prefix: "", suffix: " min", lead: "Nobody waited longer than", tail: "last month", caption: "the longest anyone waited last month" },
    claims: ["Prices on the wall.", "Evenings and Saturdays.", "New patients welcome."],
    claim: { headline: "No surprises, no rush.", accent: "no rush." },
    offer: { headline: "New patients welcome.", button: "Book a check-up" },
    document: { headline: "What the last practice charged.", title: "Check-up · invoice", totalLabel: "Not agreed first", total: "£40", lines: [
      { label: "Check-up", amount: "£45", flag: null },
      { label: "X-ray", amount: "£30", flag: "not priced first" },
      { label: "Booking fee", amount: "£10", flag: "never mentioned" },
      { label: "Hygienist", amount: "£60", flag: null },
    ] },
    painted: [
      { backdrop: "day", cast: ["shop", "person", "tree"], pose: "walk" },
      { backdrop: "office", cast: ["person", "desk"], pose: "wave", caption: "In on time, out on time.", sub: "and the price agreed first" },
    ],
  },
  {
    id: "driving-school", type: "Driving school", name: "Green Light Driving", address: "greenlightdriving.example",
    palette: ["#1c1c28", "#2a9d5c", "#fbfaf7"],
    hook: ["Test in six weeks?", "Ready in five."],
    problems: ["Lessons cancelled last minute", "A new instructor every time", "A mock test that never came"],
    steps: { headline: "How we teach.", stamp: "BOOKED", items: [
      { title: "Book a first lesson", detail: "we pick you up from home" },
      { title: "Same instructor", detail: "every single lesson" },
      { title: "A mock test first", detail: "so the real one feels familiar" },
    ] },
    figure: { value: 71, prefix: "", suffix: "%", lead: "Passed first time:", tail: "of our learners last year", caption: "of our learners passed first time last year" },
    claims: ["Same instructor.", "Dual-control cars.", "Evenings and weekends."],
    claim: { headline: "Learn to drive, calmly.", accent: "calmly." },
    offer: { headline: "First lesson half price.", button: "Book a lesson" },
    document: { headline: "What your last school didn't say.", title: "Lesson block · 10 hours", totalLabel: "Extra you paid", total: "£165", lines: [
      { label: "Ten lessons", amount: "£350", flag: null },
      { label: "Car for the test", amount: "£60", flag: "not in the price" },
      { label: "Cancelled lessons", amount: "£105", flag: "no refund" },
      { label: "Mock test", amount: "£0", flag: "never happened" },
    ] },
    painted: [
      { backdrop: "street", cast: ["houses", "van", "tree"], pose: null },
      { backdrop: "day", cast: ["person", "tree"], pose: "stand", caption: "The same instructor.", sub: "from your first lesson to your test" },
    ],
  },
];

export const exampleBusiness = (id: string) => EXAMPLE_BUSINESSES.find((b) => b.id === id);
